import type {
    LayoutSize,
    Height,
    GridTrackSize,
    Margins,
    Padding,
    MinMaxSize,
    Repeat,
} from '@wix/document-services-types'
import _ from 'lodash'

export const serializeSize = (size: LayoutSize | undefined): string | undefined => {
    if (!size) {
        return undefined
    }

    if ('value' in size) {
        switch (size.type) {
            case 'px':
                return `${size.value}px`
            case 'percentage':
                return `${size.value}%`
            case 'fr':
                return `${size.value}fr`
            case 'spx':
                return `${size.value}spx`
            case 'vh':
                return `${size.value}vh`
            case 'vw':
                return `${size.value}vw`
            case 'em':
                return `${size.value}em`
            case 'rem':
                return `${size.value}rem`
            case 'Calc':
                return `calc(${size.value})`
            default:
                return undefined
        }
    }

    switch (size.type) {
        case 'auto':
            return 'auto'
        case 'fit':
            return 'fit-content'
        case 'maxContent':
            return 'max-content'
        case 'minContent':
            return 'min-content'
        case 'unset':
            return 'unset'
        default:
            return undefined
    }
}

export const serializeHeight = (height: Height | undefined): string | undefined => {
    if (!height) {
        return undefined
    }

    if (height.type === 'aspectRatio' && 'value' in height) {
        return undefined
    }

    return serializeSize(height as LayoutSize)
}

export const serializeAspectRatio = (height: Height | undefined): string | undefined => {
    if (!height || height.type !== 'aspectRatio' || !('value' in height)) {
        return undefined
    }

    return String(height.value)
}

export const serializeRotation = (degrees: number | undefined): string | undefined => {
    if (_.isUndefined(degrees)) {
        return undefined
    }

    return `${degrees}deg`
}

export const serializeVisibility = (hidden: boolean | undefined): string | undefined => {
    if (_.isUndefined(hidden)) {
        return undefined
    }

    return hidden ? 'hidden' : 'visible'
}

const serializeMinMax = (minmax: MinMaxSize): string | undefined => {
    const min = serializeSize(minmax.min)
    const max = serializeSize(minmax.max)

    if (!min || !max) {
        return undefined
    }

    return `minmax(${min}, ${max})`
}

export function serializeGridTrack(track: GridTrackSize | undefined): string | undefined {
    if (!track) {
        return undefined
    }

    if (track.type === 'MinMaxSize') {
        return serializeMinMax(track as MinMaxSize)
    }

    if (track.type === 'Repeat') {
        return serializeRepeat(track as Repeat)
    }

    return serializeSize(track as LayoutSize)
}

function serializeRepeat(repeat: Repeat): string | undefined {
    const { length } = repeat
    const values = repeat.value.map(serializeGridTrack).filter(Boolean)

    if (values.length === 0) {
        return undefined
    }

    return `repeat(${length}, ${values.join(' ')})`
}

export const serializeGridTracks = (tracks: GridTrackSize[] | undefined): string | undefined => {
    if (!tracks || tracks.length === 0) {
        return undefined
    }

    const serialized = tracks.map(serializeGridTrack).filter(Boolean)
    return serialized.length > 0 ? serialized.join(' ') : undefined
}

export const serializeGridLine = (start: number, end: number): string => `${start} / ${end}`

export const serializeFlexDirection = (
    direction: 'row' | 'rowReverse' | 'column' | 'columnReverse' | undefined,
): string | undefined => {
    if (!direction) {
        return undefined
    }

    const mapping: Record<string, string> = {
        row: 'row',
        rowReverse: 'row-reverse',
        column: 'column',
        columnReverse: 'column-reverse',
    }

    return mapping[direction]
}

export const serializeFlexWrap = (wrap: string | undefined): string | undefined => {
    if (!wrap) {
        return undefined
    }

    const mapping: Record<string, string> = {
        nowrap: 'nowrap',
        wrap: 'wrap',
        wrapReverse: 'wrap-reverse',
    }

    return mapping[wrap] || wrap
}

export const serializeContentAlignment = (value: string | undefined): string | undefined => {
    if (!value) {
        return undefined
    }

    const mapping: Record<string, string> = {
        spaceBetween: 'space-between',
        spaceAround: 'space-around',
        spaceEvenly: 'space-evenly',
        start: 'start',
        center: 'center',
        end: 'end',
    }

    return mapping[value] || value
}

export const serializeAlignment = (value: string | undefined): string | undefined => {
    if (!value) {
        return undefined
    }

    return value
}

export const serializeAutoFlow = (value: string | undefined): string | undefined => value

export const serializeOverflow = (value: string | undefined): string | undefined => value

export const serializeMargins = (margins: Margins | undefined): Record<string, string | undefined> => {
    if (!margins) {
        return {}
    }

    return {
        'margin-top': serializeSize(margins.top),
        'margin-bottom': serializeSize(margins.bottom),
        'margin-left': serializeSize(margins.left),
        'margin-right': serializeSize(margins.right),
    }
}

export const serializePadding = (padding: Padding | undefined): Record<string, string | undefined> => {
    if (!padding) {
        return {}
    }

    return {
        'padding-top': serializeSize(padding.top),
        'padding-bottom': serializeSize(padding.bottom),
        'padding-left': serializeSize(padding.left),
        'padding-right': serializeSize(padding.right),
    }
}
